import { useState } from 'react';
import ConfirmModal from './ConfirmModal';

export default function HuertoGrid({ huerto, onCaballonClick, onRemoveSeed, onReset }) {
  const { caballones, longitud, caballonesData } = huerto;
  const [expanded, setExpanded] = useState({});
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const renderListView = () => {
    let allPlanted = [];
    Object.keys(caballonesData).forEach(cIndex => {
      caballonesData[cIndex].forEach(item => {
        allPlanted.push({ ...item, caballonIndex: parseInt(cIndex) });
      });
    });

    if (allPlanted.length === 0) {
      return <div className="text-center mt-4 text-secondary">No hay semillas plantadas en el huerto.</div>;
    }

    allPlanted.sort((a, b) => {
      const nameCompare = a.seed.name.localeCompare(b.seed.name);
      if (nameCompare !== 0) return nameCompare;
      const varietyCompare = (a.variety || '').localeCompare(b.variety || '');
      if (varietyCompare !== 0) return varietyCompare;
      return a.caballonIndex - b.caballonIndex;
    });

    return (
      <div className="planted-list animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {allPlanted.map((item, idx) => (
          <div key={`${item.seed.id}-${item.caballonIndex}-${item.side}-${idx}`} className="planted-item" style={{ backgroundColor: 'var(--surface-color)', padding: '16px' }}>
            <span className="planted-item-icon" style={{ fontSize: '32px', marginRight: '16px' }}>{item.seed.icon}</span>
            <span className="planted-item-text">
              <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '4px' }}>
                {item.quantity} {item.seed.name} {item.variety && `(${item.variety})`}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                🌾 Caballón {item.caballonIndex + 1} • Lado {(item.side || 'left') === 'left' ? 'Izquierdo' : 'Derecho'}
              </div>
              {item.date && (
                <div style={{ fontSize: '12px', color: 'var(--primary-color)', marginTop: '4px', fontWeight: 600 }}>
                  📅 {new Date(item.date).toLocaleDateString()}
                </div>
              )}
            </span>
            <button 
              className="btn-remove"
              style={{ padding: '8px' }}
              onClick={(e) => {
                e.stopPropagation();
                setItemToDelete({
                  caballonIndex: item.caballonIndex,
                  seedId: item.seed.id,
                  side: item.side || 'left',
                  variety: item.variety,
                  seedName: `${item.seed.name}${item.variety ? ` (${item.variety})` : ''}`
                });
                setConfirmDeleteOpen(true);
              }}
            >
              &times;
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="huerto-container animate-fade">
      <div className="flex-between" style={{ marginBottom: '12px' }}>
        <h2 style={{ margin: 0 }}>Mi Huerto</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div className="side-toggle" style={{ width: 'auto', padding: '2px' }}>
            <button className={`side-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} style={{ padding: '4px 8px', fontSize: '12px' }}>Gráfica</button>
            <button className={`side-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} style={{ padding: '4px 8px', fontSize: '12px' }}>Lista</button>
          </div>

        </div>
      </div>
      
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px', paddingBottom: '20px' }}>
        {viewMode === 'list' ? renderListView() : Array.from({ length: caballones }).map((_, cIndex) => {
          const rowData = caballonesData[cIndex];
          const leftData = rowData.filter(i => (i.side || 'left') === 'left');
          const rightData = rowData.filter(i => i.side === 'right');

          const renderProgressBar = (data, availableSpace, label, side) => (
            <div 
              style={{ marginBottom: '8px', cursor: 'pointer' }}
              onClick={(e) => { e.stopPropagation(); onCaballonClick(cIndex, side); }}
            >
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px', display: 'flex', justifyContent: 'space-between' }}>
                <span>{label}</span>
                <span style={{ color: availableSpace <= 0 ? 'var(--danger-color)' : 'inherit' }}>
                  {availableSpace < 0 ? `Exceso de ${Math.abs(availableSpace)} cm` : `${availableSpace} cm libres`}
                </span>
              </div>
              <div className="caballon-progress-bar" style={{ height: '24px', marginBottom: 0 }}>
                {data.map((item) => {
                  const widthPct = ((item.quantity * item.seed.distanceCm) / longitud) * 100;
                  return (
                    <div 
                      key={`${item.seed.id}-${item.variety || ''}`} 
                      className="progress-segment"
                      style={{ width: `${widthPct}%` }}
                      title={`${item.seed.name}${item.variety ? ` (${item.variety})` : ''}: ${item.quantity} plantas (${item.quantity * item.seed.distanceCm} cm)`}
                    >
                      <span className="progress-icon" style={{ fontSize: '12px' }}>{item.seed.icon}</span>
                    </div>
                  );
                })}
                {availableSpace > 0 && (
                  <div 
                    className="progress-segment empty" 
                    style={{ width: `${(availableSpace / longitud) * 100}%` }}
                  />
                )}
              </div>
            </div>
          );

          return (
            <div key={`c-${cIndex}`} className="caballon" onClick={() => onCaballonClick(cIndex, 'left')}>
              <div className="caballon-header flex-between">
                <span style={{ fontWeight: 600 }}>🌾 Caballón {cIndex + 1}</span>
                {rowData.length > 0 && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); setExpanded(prev => ({ ...prev, [cIndex]: !prev[cIndex] })); }}
                    style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    {expanded[cIndex] ? 'Ocultar plantas ▲' : `Ver plantas (${rowData.length}) ▼`}
                  </button>
                )}
              </div>
              
              {/* Progress Bars */}
              <div style={{ marginBottom: '12px' }}>
                {renderProgressBar(leftData, longitud - leftData.reduce((acc, item) => acc + (item.quantity * item.seed.distanceCm), 0), 'Lado Izquierdo', 'left')}
                {renderProgressBar(rightData, longitud - rightData.reduce((acc, item) => acc + (item.quantity * item.seed.distanceCm), 0), 'Lado Derecho', 'right')}
              </div>

              {/* List of seeds planted */}
              {rowData.length > 0 && expanded[cIndex] && (
                <div className="planted-list animate-fade">
                  {rowData.map(item => (
                    <div key={`${item.seed.id}-${item.side || 'left'}-${item.variety || ''}`} className="planted-item">
                      <span className="planted-item-icon">{item.seed.icon}</span>
                      <span className="planted-item-text">
                        <div>
                          {item.quantity} {item.seed.name} {item.variety && `(${item.variety})`} 
                          <span style={{ fontSize: '10px', marginLeft: '6px', backgroundColor: 'rgba(255,255,255,0.1)', padding: '2px 4px', borderRadius: '4px' }}>
                            {(item.side || 'left') === 'left' ? 'Izq.' : 'Der.'}
                          </span>
                        </div>
                        {item.date && <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>📅 {new Date(item.date).toLocaleDateString()}</div>}
                      </span>
                      <button 
                        className="btn-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          setItemToDelete({
                            caballonIndex: cIndex,
                            seedId: item.seed.id,
                            side: item.side || 'left',
                            variety: item.variety,
                            seedName: `${item.seed.name}${item.variety ? ` (${item.variety})` : ''}`
                          });
                          setConfirmDeleteOpen(true);
                        }}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ConfirmModal
        isOpen={confirmDeleteOpen}
        title="Eliminar Plantación"
        message={itemToDelete ? `¿Seguro que quieres eliminar ${itemToDelete.seedName} del Caballón ${itemToDelete.caballonIndex + 1}?` : ''}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={() => {
          if (itemToDelete) {
            onRemoveSeed(itemToDelete.caballonIndex, itemToDelete.seedId, itemToDelete.side, itemToDelete.variety);
          }
          setConfirmDeleteOpen(false);
          setItemToDelete(null);
        }}
        onCancel={() => {
          setConfirmDeleteOpen(false);
          setItemToDelete(null);
        }}
        icon="🗑️"
      />
    </div>
  );
}
